// ============================================================================
// ¿El usuario opera Cobranza? (#1264)
// ============================================================================
// Misma regla que el backend (`assertCobranzaOperator`, utils/userManagementAuthz) para que la
// pantalla y la API no discrepen: opera Cobranza el administrador de plataforma
// (incluye al owner) o el rol `finance`, reconocido SÓLO por su id fijo.
//
// El rol se toma del usuario en memoria o, si aún no carga, del `userRoleId` que
// el login guarda en localStorage. La API vuelve a decidir con el rol de
// Firestore: esto sólo decide qué se muestra.
// ============================================================================
import { useUserStore } from "../store/userStore";
import { isPlatformAdminUser } from "./useIsReseller";

/** Id fijo del rol de finanzas (el que crea `scripts/seed-finance-role.js`). */
export const FINANCE_ROLE_ID = "finance";

/** ¿Es el rol finance? SÓLO por su id fijo (un rol llamado así con otro id no cuenta). */
export function isFinanceRole(roleId: string | null | undefined): boolean {
  return roleId === FINANCE_ROLE_ID;
}

/** Llave de la app en el catálogo de derechos. */
export const COBRANZA_ENTITLEMENT = "app:cobranza";

/**
 * ¿La bandera global de Cobranza está APAGADA? Es el interruptor de emergencia y
 * vale para todos, también para el rol finance. (Que la app sea `internal` no lo
 * detiene: por eso su acceso sale del rol y no del catálogo.)
 */
export function cobranzaSwitchedOff(reason: string | null | undefined): boolean {
  return reason === "flag-off";
}

/** Regla pura: admin de plataforma (con owner) o finance. */
export function isCobranzaOperatorUser(roleId?: string | null): boolean {
  if (isPlatformAdminUser()) return true;
  const id = roleId ?? localStorage.getItem("userRoleId");
  return isFinanceRole(id);
}

/**
 * ¿Opera Cobranza SIN ser de plataforma? (el rol finance). Sirve para esconderle
 * lo que en Cobranza sigue siendo de plataforma: dar de alta o editar compañías,
 * fuentes, retransmisiones y las pantallas de usuarios y dispositivos.
 */
export function isFinanceOnlyUser(roleId?: string | null): boolean {
  if (isPlatformAdminUser()) return false;
  return isFinanceRole(roleId ?? localStorage.getItem("userRoleId"));
}

/** Hook: se recalcula cuando cambia el usuario en memoria. */
export function useIsCobranzaOperator(): boolean {
  const roleId = useUserStore((state) => state.user?.role_id) as string | undefined;
  return isCobranzaOperatorUser(roleId || localStorage.getItem("userRoleId"));
}

/** Hook: ¿es finance y no plataforma? */
export function useIsFinanceOnly(): boolean {
  const roleId = useUserStore((state) => state.user?.role_id) as string | undefined;
  return isFinanceOnlyUser(roleId || localStorage.getItem("userRoleId"));
}

export default useIsCobranzaOperator;
